import React from 'react'
import forge from 'node-forge'
import streamSaver from 'streamsaver'
import { Button, Modal, Progress } from 'antd'
import { confirmBase, PasswordInput } from 'utils/ui'
import { blobToBase64, decryptBlobAndToBase64, downloadFile } from 'utils/importExport/utils/export'
import { getCachedFile } from 'utils/attachmentCache'
import { getRsKeyObjFromResource } from 'pages/records/components/RecordCardContent'
import ResourceService from 'api/Resource'
import { SERVICE_TITLE, SERVICE_NAME } from 'service'
import { FILE_ACCESS_LEVEL } from 'types/utils'
import classNames from 'classnames'
import i18n from 'i18next'
import { getExportTextKeys } from './exportTextKeys'
import styles from './style.module.scss'
import ExportFormatField, { TemplateSelectContent } from './ExportCsv/ExportCsvFields'
import {
  isCsvExportableRecord,
  getTemplateId,
  groupRecordsByTemplate,
  buildCsvPlan,
  buildCsvRow,
  formatCsvLine,
  getCsvHeaderBlock,
  getTemplateDisplayName,
} from './csvExport'

export const EXPORT_VERSION = '1.1'

export const formatElapsed = (ms) => {
  const totalSec = Math.round(ms / 1000)
  if (totalSec < 60) return `${totalSec}초`
  return `${Math.floor(totalSec / 60)}분 ${totalSec % 60}초`
}

const showExportResultModal = (content, t, elapsedMs = null) => {
  const isDev = process.env.REACT_APP_TARGET !== 'production'
  const modalContent =
    isDev && elapsedMs != null ? (
      <div>
        <div>{content}</div>
        <div style={{ marginTop: 8, fontSize: 12, color: '#999' }}>
          소요 시간: {formatElapsed(elapsedMs)} (개발버전에서만 표시)
        </div>
      </div>
    ) : (
      content
    )

  confirmBase({
    wrapClassName: classNames(styles.decryptModal),
    icon: 'black',
    title: <div className={styles.decryptTitle}>{t('record.title.exportCompleted')}</div>,
    content: modalContent,
    okText: t('common.button.ok'),
    okButtonProps: { className: classNames('type7', 'w100', 'h38') },
    cancelButtonProps: { style: { display: 'none' } },
  })
}

const showFailedRecordsModal = (failedRecords, t) => {
  const getReasonText = (record) => {
    if (record.reason === 'files' && record.files?.length > 0) {
      return `${t('record.desc.exportFailedFilesTitle')}: ${record.files.join(', ')}`
    }
    if (record.reason === 'rsa_key') {
      return t('record.desc.exportFailedRsaKey')
    }
    if (record.reason === 'decrypt') {
      return t('record.desc.exportFailedDecrypt')
    }
    return t('record.desc.exportFailedUnknown')
  }

  confirmBase({
    icon: 'red',
    title: t('record.desc.exportSuccessWithFailures'),
    width: 480,
    content: (
      <div className={styles.failedModal}>
        <div className={styles.listTitle}>
          {t('record.desc.exportFailedRecordsList', { count: failedRecords.length })}
        </div>
        <div className={styles.recordList}>
          {failedRecords.map((record, idx) => (
            <div
              key={`failed-${record.title}-${record.reason}-${idx}`} // eslint-disable-line react/no-array-index-key
              className={styles.recordItem}
            >
              <div className={styles.recordTitle}>{record.title}</div>
              <div className={styles.recordReason}>{getReasonText(record)}</div>
            </div>
          ))}
        </div>
      </div>
    ),
    okText: t('common.button.ok'),
    cancelButtonProps: { style: { display: 'none' } },
  })
}

export const createSemaphore = (limit) => {
  let active = 0
  const queue = []
  return {
    acquire: () =>
      new Promise((resolve) => {
        if (active < limit) {
          active += 1
          resolve()
        } else {
          queue.push(resolve)
        }
      }),
    release: () => {
      active -= 1
      if (queue.length > 0) {
        active += 1
        queue.shift()()
      }
    },
  }
}

// 내보내기에 담을 수 있는 파일당 상한. export 는 파일 바이트를 base64 로 페이로드에 넣으므로
// (processFile 의 chunkBase64) 대용량은 담을 수 없다 — 500MB 는 base64 로 665MB 가 되고
// RECORD_CONCURRENCY(3) 병렬이면 2GB 다. 미리보기·캐시 상한과 같은 50MB 로 맞춰
// "50MB 넘는 파일은 미리보기·캐시·내보내기에서 빠진다"로 사용자 설명이 하나가 되게 했다.
// HEAVY_SIZE_THRESHOLD(20MB)를 재사용하지 않는 이유: 그 값은 "동시 처리 메모리 추정"이라 의미가 다르다.
export const EXPORTABLE_FILE_SIZE_LIMIT = 50 * 1024 * 1024

// utils 의 formatBytes 를 쓰지 않는 이유: 그 모듈이 ESM 의존을 끌어와 기존 bulkExport.test.js
// suite 가 파싱 단계에서 실패한다. 표시용 한 줄이라 지역 포매터로 둔다(records/helper.js 전례).
const formatSizeLimitLabel = (bytes) => `${Math.round(bytes / (1024 * 1024))}MB`

export const processRecord = async (
  record,
  fileSemaphore = null,
  allowFileDownload = true,
  alreadyDecrypted = false,
) => {
  const failedFiles = []
  // 실패(failedFiles)와 **정책상 제외**를 분리한다 — failedFiles 는 exportPartialSuccess·
  // exportFailedFilesTitle 같은 오류 문구로 나가므로, 의도된 제외를 태우면 사용자가 실패로 오독한다.
  const omittedFiles = []
  let rsKeyObj = null

  try {
    rsKeyObj = await getRsKeyObjFromResource(record)
  } catch (err) {
    console.error(`Failed to get RSA key for record: ${record.title}`, err)
    throw new Error('rsa_key_error')
  }

  const items = await record.items.reduce(async (accPromise, item, idx) => {
    const acc = await accPromise
    const exportItem = {
      type: item.type,
      label: item.label,
      content: item.content,
      custom: item.custom || false,
      encrypt: item.encrypt || false,
      required: item.required || false,
      order: idx + 1,
    }

    // alreadyDecrypted: 상세 화면이 넘긴 레코드는 content 가 이미 평문이고 encrypt 는 true 로 남아 있다(RecordWrapper.js:146).
    // 다시 복호화하면 대부분 예외가 나 CSV 가 그 칸을 비우고, 일부 길이는 예외 없이 빈 문자열이 돌아와 표시 없이 비어 나온다
    if (
      !alreadyDecrypted &&
      item.encrypt === true &&
      item.type !== 'file' &&
      item.content != null
    ) {
      try {
        exportItem.content = await rsKeyObj.decrypt(item.content)
      } catch (err) {
        console.error(`Failed to decrypt item: ${item.label} in record: ${record.title}`, err)
        exportItem.decryptFailed = true
      }
    }

    if (!allowFileDownload && item.type === 'files') {
      exportItem.content = []
    }

    if (
      allowFileDownload &&
      item.type === 'files' &&
      Array.isArray(item.content) &&
      item.content.length > 0
    ) {
      const convertedFiles = []

      const processFile = async (file) => {
        // 상한 초과는 **다운로드도 시작하지 않는다** — 목적이 페이로드·힙 절약이므로 받아본 뒤
        // 버리면 의미가 없다. null 이 아니라 마커 항목을 남기는 이유: 가져오기 쪽이 "파일이
        // 있었지만 빠졌다"를 알 수 있어야 하고, 그래야 사용자에게 설명할 수 있다.
        if (typeof file.size === 'number' && file.size > EXPORTABLE_FILE_SIZE_LIMIT) {
          omittedFiles.push(file.filename || 'unknown')
          return {
            filename: file.filename,
            size: file.size,
            content_type: file.content_type,
            encrypt: file.encrypt || false,
            // ImportV2 의 CSV 행 제외(excluded/excludedRows)와 구별되는 이름을 쓴다
            sizeExcluded: true,
          }
        }

        if (!file.url) {
          failedFiles.push(file.filename || 'unknown')
          return null
        }
        try {
          const cacheKey = file.url || `${file.filename}_${file.size}`
          let blob = await getCachedFile(cacheKey)
          const fromCache = !!blob

          if (!blob) {
            if (fileSemaphore) await fileSemaphore.acquire()
            try {
              let downloadUrl = file.download_url

              if (!downloadUrl) {
                const parts = file.url?.split('/file/')
                const objectKey = parts?.length > 1 ? parts[1] : null
                if (objectKey && record.id) {
                  const result = await ResourceService.getDownloadUrls(
                    [{ resource_id: record.id, object_key: objectKey }],
                    'download',
                    true,
                  )
                  downloadUrl = result.data?.files?.[0]?.download_url
                }
              }
              if (!downloadUrl) throw new Error('no_download_url')
              const response = await fetch(downloadUrl)
              if (!response.ok) throw new Error(`HTTP ${response.status}`)

              blob = await response.blob()
            } finally {
              if (fileSemaphore) fileSemaphore.release()
            }
          }

          let base64
          if (file.encrypt && !fromCache) {
            base64 = await decryptBlobAndToBase64(blob, rsKeyObj)
          } else {
            base64 = await blobToBase64(blob)
          }

          return {
            filename: file.filename,
            size: file.size,
            content_type: file.content_type,
            encrypt: file.encrypt || false,
            data: chunkBase64(base64),
          }
        } catch (err) {
          console.error(`Failed to download file: ${file.filename}`, err)
          failedFiles.push(file.filename || 'unknown')
          return null
        }
      }

      const results = await Promise.all(item.content.map(processFile))
      results.forEach((r) => r !== null && convertedFiles.push(r))

      exportItem.content = convertedFiles
    }

    acc.push(exportItem)
    return acc
  }, Promise.resolve([]))

  return {
    exportRecord: {
      template: {
        id: record.template?.id || record.template_id,
        name: record.template?.name || '',
        icon: record.template?.icon || record.template_icon || '',
        required_type: record.template?.required_type || '',
      },
      title: record.title,
      items,
    },
    failedFiles,
    omittedFiles,
  }
}

export const makeProgressContent = (current, total, t) => (
  <div className={styles.progressModal}>
    <Progress
      percent={total > 0 ? Math.round((current / total) * 100) : 0}
      status={current < total ? 'active' : 'success'}
    />
    <div className={styles.progressCount}>
      {current} / {total}
    </div>
    <div className={styles.progressWarning}>{t('topBar.bulkExport.progressWarning')}</div>
  </div>
)

export const computeSHA256Hex = async (str) => {
  const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(str))
  return Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')
}

// 단일 레코드의 최대 파일 용량 합산 → 동시 처리 메모리 압박 기준
export const getMaxRecordFileSize = (records) =>
  records.reduce((max, r) => {
    const recordSize = r.items
      .filter((i) => i.type === 'files' && Array.isArray(i.content))
      .reduce((s, i) => s + i.content.reduce((fs, f) => fs + (f.size || 0), 0), 0)
    return Math.max(max, recordSize)
  }, 0)

// 단일 레코드 파일 용량이 임계값 초과 시 순차(1), 아니면 병렬(3)
// 동시 메모리 ≈ threshold × RECORD_CONCURRENCY(3) × 1.33(base64) ≈ 20MB
export const HEAVY_SIZE_THRESHOLD = 20 * 1024 * 1024 // 20MB (10MB 파일 2개 수준, 동시 메모리 ≈ 80MB)

const processRecordsLoop = async (
  records,
  progressModal,
  t,
  onRecord,
  allowFileDownload = true,
) => {
  const FILE_CONCURRENCY = 3
  const maxRecordFileSize = allowFileDownload ? getMaxRecordFileSize(records) : 0
  const RECORD_CONCURRENCY = maxRecordFileSize > HEAVY_SIZE_THRESHOLD ? 1 : 3

  const fileSemaphore = createSemaphore(FILE_CONCURRENCY)
  const recordSemaphore = createSemaphore(RECORD_CONCURRENCY)
  const failedRecords = []
  // 정책상 제외된 파일 수 — 실패와 별개 통로로 완료 안내에 합산해 보고한다
  let omittedFileCount = 0

  // 처리 완료된 결과를 인덱스별로 보관 (write는 원본 순서대로)
  const resultBuffer = new Array(records.length).fill(undefined)
  let writeIndex = 0
  let completedCount = 0
  let flushChain = Promise.resolve()

  // 연속된 완료 결과를 순서대로 write (promise chain으로 직렬화)
  const flushBuffer = () => {
    flushChain = flushChain.then(async () => {
      while (writeIndex < records.length && resultBuffer[writeIndex] !== undefined) {
        const { record, exportRecord, failedFiles, omittedFiles, error } = resultBuffer[writeIndex]
        const currentIndex = writeIndex
        writeIndex += 1

        if (error) {
          console.error(`Failed to process record: ${record.title}`, error)
          failedRecords.push({
            title: record.title,
            reason: error.message?.includes('rsa_key') ? 'rsa_key' : 'decrypt',
            error: error.message,
          })
        } else {
          // eslint-disable-next-line no-await-in-loop
          const outcome = await onRecord(exportRecord, currentIndex)
          // CSV 는 복호화에 실패한 항목을 빈 칸으로 쓰고 그 레코드를 실패 목록에 올린다(spec D6).
          // JSON 경로의 onRecord 는 undefined 를 돌려주므로 이 줄은 아무 일도 하지 않는다
          if (outcome?.failReason) {
            failedRecords.push({ title: record.title, reason: outcome.failReason })
          }
          if (failedFiles.length > 0) {
            failedRecords.push({ title: record.title, reason: 'files', files: failedFiles })
          }
          // 정책상 제외는 실패 목록과 섞지 않고 별도로 센다 (벌크 완료 안내에서 합산 표시)
          if (omittedFiles?.length > 0) {
            omittedFileCount += omittedFiles.length
          }
        }
      }
    })
  }

  // Worker Pool: 슬롯 빈 즉시 다음 레코드 처리 (처리 완료 시 슬롯 반환)
  await Promise.all(
    records.map(async (record, index) => {
      await recordSemaphore.acquire()
      try {
        const { exportRecord, failedFiles, omittedFiles } = await processRecord(
          record,
          fileSemaphore,
          allowFileDownload,
        )
        resultBuffer[index] = { record, exportRecord, failedFiles, omittedFiles, error: null }
      } catch (err) {
        resultBuffer[index] = {
          record,
          exportRecord: null,
          failedFiles: [],
          omittedFiles: [],
          error: err,
        }
      } finally {
        completedCount += 1
        progressModal?.update({ content: makeProgressContent(completedCount, records.length, t) })
        recordSemaphore.release()
        flushBuffer()
      }
    }),
  )

  await flushChain
  return { failedRecords, omittedFileCount }
}

const chunkBase64 = (str, size = 76) => str.match(new RegExp(`.{1,${size}}`, 'g')) ?? []

export const encryptForExport = async (plainText, password) => {
  const data = new TextEncoder().encode(plainText)
  const salt = crypto.getRandomValues(new Uint8Array(16))
  const iv = crypto.getRandomValues(new Uint8Array(16))

  const passwordRaw = new Uint8Array(password.length)
  for (let i = 0; i < password.length; i += 1) {
    passwordRaw[i] = password.charCodeAt(i) & 0xff // eslint-disable-line no-bitwise
  }

  const keyMaterial = await crypto.subtle.importKey('raw', passwordRaw, { name: 'PBKDF2' }, false, [
    'deriveKey',
  ])
  const key = await crypto.subtle.deriveKey(
    { name: 'PBKDF2', salt, iterations: 100000, hash: 'SHA-256' },
    keyMaterial,
    { name: 'AES-CBC', length: 256 },
    false,
    ['encrypt'],
  )

  const ciphertext = await crypto.subtle.encrypt({ name: 'AES-CBC', iv }, key, data)

  const combined = new Uint8Array(32 + ciphertext.byteLength)
  combined.set(salt, 0)
  combined.set(iv, 16)
  combined.set(new Uint8Array(ciphertext), 32)

  let binary = ''
  const CHUNK = 8192
  for (let i = 0; i < combined.length; i += CHUNK) {
    binary += String.fromCharCode(...combined.subarray(i, i + CHUNK))
  }
  return btoa(binary)
}

// 파일 저장 경로 선택 (AbortError: null 반환으로 취소 표시, 미지원 브라우저: undefined 반환)
const pickSaveFileHandle = async (suggestedName, isEncrypted, format = 'json') => {
  if (typeof window.showSaveFilePicker !== 'function') return undefined
  try {
    return await window.showSaveFilePicker({
      suggestedName,
      types: [
        format === 'csv'
          ? { description: 'CSV', accept: { 'text/csv': ['.csv'] } }
          : {
              description: 'JSON',
              accept: isEncrypted ? { 'text/plain': ['.enc'] } : { 'application/json': ['.json'] },
            },
      ],
    })
  } catch (e) {
    if (e.name === 'AbortError') return null
    return undefined
  }
}

const showFileAccessLimitModal = (t) =>
  new Promise((resolve) => {
    confirmBase({
      icon: 'red',
      title: t('topBar.bulkExport.fileAccessLimitTitle'),
      content: t('topBar.bulkExport.fileAccessLimitContent'),
      onOk: () => resolve(true),
      onCancel: () => resolve(false),
    })
  })

// 단일 레코드를 export 포맷(JSON + 체크섬)으로 조립
const buildExportData = async (exportRecord, password) => {
  const isEncrypted = !!password
  const recordJson = JSON.stringify(exportRecord)
  const md = forge.md.sha256.create()
  let records

  if (password) {
    const encBase64 = await encryptForExport(recordJson, password)
    md.update(encBase64)
    records = [chunkBase64(encBase64)]
  } else {
    md.update(recordJson)
    records = [exportRecord]
  }

  return {
    version: EXPORT_VERSION,
    exportedAt: new Date().toISOString(),
    ...(isEncrypted && { encrypted: true }),
    records,
    checksum: md.digest().toHex(),
  }
}

// ── CSV 내보내기 보조 함수(spec D1~D12) — 단건·일괄 CSV 가 함께 쓴다 ─────────────────────

// 템플릿 상세는 가져오기 모듈의 fetchTemplateSchema 를 쓴다. bulkImport.js 가 bulkExport.js 를 정적으로 가져와서
// (bulkImport.js:26-33) 여기서 정적으로 import 하면 순환이므로 호출 시점에 dynamic import 한다.
// 실패한 템플릿은 스키마 없이 계속한다 — 헤더가 저장 label 이 될 뿐 데이터는 잃지 않는다(spec F1)
const loadTemplateSchemas = async (templateIds) => {
  let fetchTemplateSchema
  try {
    const bulkImportModule = await import('utils/importExport/bulkImport')
    fetchTemplateSchema = bulkImportModule.fetchTemplateSchema
  } catch (err) {
    console.error('Template schema module load failed:', err)
    return {}
  }
  const entries = await Promise.all(
    templateIds.map(async (templateId) => {
      try {
        return [templateId, await fetchTemplateSchema(templateId)]
      } catch (err) {
        console.error('Template schema fetch failed:', templateId, err)
        return null
      }
    }),
  )
  return Object.fromEntries(entries.filter(Boolean))
}

// 쓰기 도중 실패했을 때 평문 부분 파일이 남지 않게 writer 를 정리한다. 정리 실패가 원래 오류를 가리지 않도록 삼킨다
const abortWritableQuietly = async (writable) => {
  try {
    await writable?.abort?.()
  } catch (err) {
    // 이미 닫혔거나 abort 를 지원하지 않는 writer — 원래 오류가 우선이다
  }
}

// 단건 CSV — 일괄과 같은 조립 함수를 쓰고 한 행만 쓴다(spec D10). 메모는 호출부가 csvAllowed 로 막는다
export const executeSingleCsvExport = async ({
  individual,
  filename,
  t,
  messageApi,
  setExportLoading,
  onDestroyParentModals,
}) => {
  const exportedFilename = `${filename}.csv`
  const fileHandle = await pickSaveFileHandle(exportedFilename, false, 'csv')
  if (fileHandle === null) return false

  setExportLoading(true)
  messageApi.loading({
    content: t('record.desc.exportFileDownloading'),
    key: 'exportFileDownloading',
    duration: 0,
  })

  let writable = null
  try {
    // 첨부는 받지 않는다(allowFileDownload=false) — CSV 에는 첨부 열이 없다.
    // individual 은 상세 화면이 이미 복호화한 레코드라서 다시 복호화하지 않는다(alreadyDecrypted=true).
    // 대가: getData 가 복호화 실패를 삼키므로(RecordWrapper.js:152-154) 복호화에 실패해 암호문이 남은 항목은
    // 암호문 그대로 나가고 아래 hasDecryptFailure 경고 분기에는 닿지 않는다(audit §A10)
    const { exportRecord } = await processRecord(individual, null, false, true)
    const templateId = getTemplateId(individual)
    const schemaByTemplateId = await loadTemplateSchemas([templateId].filter(Boolean))
    const locale = (i18n.language || 'ko-KR').replace('-', '_')
    const plan = buildCsvPlan([individual], {
      schemaByTemplateId,
      templateColumnHeader: t('record.field.exportCsvTemplateColumn'),
      extraColumnHeader: t('record.field.exportCsvOtherColumn'),
    })
    const { cells, hasDecryptFailure } = buildCsvRow(
      exportRecord,
      plan,
      getTemplateDisplayName(
        schemaByTemplateId[templateId]?.translations,
        locale,
        exportRecord.template.name,
      ),
    )
    const content = getCsvHeaderBlock(plan.headers) + formatCsvLine(cells)

    if (fileHandle) {
      writable = await fileHandle.createWritable()
      await writable.write(content)
      await writable.close()
    } else {
      downloadFile(content, exportedFilename, 'text/csv;charset=utf-8')
    }

    onDestroyParentModals?.()

    if (hasDecryptFailure) {
      messageApi.warning(t('record.desc.exportFailedDecrypt'))
    } else {
      messageApi.open({
        type: 'success',
        content: t('record.result.exportSuccess', { service: t(SERVICE_NAME) }),
      })
    }
    return true
  } catch (err) {
    console.error('Single CSV export error:', err)
    await abortWritableQuietly(writable)
    messageApi.error(t('record.error.exportFailed'))
    return false
  } finally {
    messageApi.destroy('exportFileDownloading')
    setExportLoading(false)
  }
}

export const executeSingleExport = async ({
  individual,
  filename,
  password,
  t,
  messageApi,
  setExportLoading,
  allowFileDownload = true,
  onDestroyParentModals,
  // 'csv' 면 단건 CSV 로 위임한다(spec D10). 기본 'json' 이라 기존 호출부의 동작은 그대로다
  format = 'json',
}) => {
  // 파일 접근 제한 확인보다 앞이다 — CSV 는 첨부를 받지 않아 그 확인이 필요 없다
  if (format === 'csv') {
    return executeSingleCsvExport({
      individual,
      filename,
      t,
      messageApi,
      setExportLoading,
      onDestroyParentModals,
    })
  }

  if (!allowFileDownload) {
    onDestroyParentModals?.()
    const confirmed = await showFileAccessLimitModal(t)
    if (!confirmed) return false
  }

  const isEncrypted = !!password
  const exportedFilename = `${filename}${isEncrypted ? '.json.enc' : '.json'}`
  const mimeType = isEncrypted ? 'text/plain' : 'application/json'

  const fileHandle = await pickSaveFileHandle(exportedFilename, isEncrypted)
  if (fileHandle === null) return false

  setExportLoading(true)
  messageApi.loading({
    content: t('record.desc.exportFileDownloading'),
    key: 'exportFileDownloading',
    duration: 0,
  })

  try {
    // individual 은 상세 화면이 이미 복호화한 레코드라서 다시 복호화하지 않는다(alreadyDecrypted=true) — 단건 CSV 와 같다
    const { exportRecord, failedFiles, omittedFiles } = await processRecord(
      individual,
      null,
      allowFileDownload,
      true,
    )
    if (!exportRecord) return false

    const exportData = await buildExportData(exportRecord, password)
    const content = JSON.stringify(exportData, null, 2)

    if (fileHandle) {
      const writable = await fileHandle.createWritable()
      await writable.write(content)
      await writable.close()
    } else {
      downloadFile(content, exportedFilename, mimeType)
    }

    onDestroyParentModals?.()

    // 제외 안내는 실패와 **독립적으로** 띄운다 — 둘은 다른 사건이고, 실패가 있어도 제외 사실은
    // 알려야 한다(그 파일이 왜 복원되지 않는지 설명이 필요하다)
    if (omittedFiles.length > 0) {
      messageApi.warning(
        t('record.desc.exportFileExcluded', {
          count: omittedFiles.length,
          limit: formatSizeLimitLabel(EXPORTABLE_FILE_SIZE_LIMIT),
        }),
      )
    }

    if (failedFiles.length > 0) {
      messageApi.warning(t('record.desc.exportPartialSuccess', { files: failedFiles.join(', ') }))
    } else {
      messageApi.open({
        type: 'success',
        content: isEncrypted
          ? t('record.result.exportEncryptedSuccess')
          : t('record.result.exportSuccess', { service: t(SERVICE_NAME) }),
      })
    }
    return true
  } catch (err) {
    console.error('Export error:', err)
    messageApi.error(t('record.error.exportFailed'))
    return false
  } finally {
    messageApi.destroy('exportFileDownloading')
    setExportLoading(false)
  }
}

const streamRecords = async (writable, records, progressModal, t, allowFileDownload = true) => {
  const enc = new TextEncoder()
  const md = forge.md.sha256.create()
  let isFirst = true

  await writable.write(
    enc.encode(`{
  "version": "${EXPORT_VERSION}",
  "exportedAt": "${new Date().toISOString()}",
  "records": [\n`),
  )

  const { failedRecords, omittedFileCount } = await processRecordsLoop(
    records,
    progressModal,
    t,
    async (exportRecord) => {
      const recordJson = JSON.stringify(exportRecord, null, 2).replace(/^/gm, '    ')
      await writable.write(enc.encode(`${isFirst ? '' : ',\n'}${recordJson}`))
      md.update(recordJson)
      isFirst = false
    },
    allowFileDownload,
  )

  const checksum = md.digest().toHex()
  await writable.write(enc.encode(`\n  ],\n  "checksum": "${checksum}"\n}`))
  await writable.close()
  return { failedRecords, omittedFileCount }
}

const streamEncryptedRecords = async (
  writable,
  records,
  password,
  progressModal,
  t,
  allowFileDownload = true,
) => {
  const enc = new TextEncoder()
  const md = forge.md.sha256.create()
  let isFirst = true

  await writable.write(
    enc.encode(`{
  "version": "${EXPORT_VERSION}",
  "exportedAt": "${new Date().toISOString()}",
  "encrypted": true,
  "records": [\n`),
  )

  const { failedRecords, omittedFileCount } = await processRecordsLoop(
    records,
    progressModal,
    t,
    async (exportRecord) => {
      const encBase64 = await encryptForExport(JSON.stringify(exportRecord), password)
      await writable.write(
        enc.encode(`${isFirst ? '' : ',\n'}    ${JSON.stringify(chunkBase64(encBase64))}`),
      )
      md.update(encBase64)
      isFirst = false
    },
    allowFileDownload,
  )

  const checksum = md.digest().toHex()
  await writable.write(enc.encode(`\n  ],\n  "checksum": "${checksum}"\n}`))
  await writable.close()

  return { failedRecords, omittedFileCount }
}

// 대량 조회를 100건씩 끝까지 돌려 모은다 — JSON 과 CSV 내보내기가 같은 조회를 쓴다.
// 첫 응답의 fileAccessLevel 만 쓴다(첨부 다운로드 허용 여부는 호출 사이에 바뀌지 않는다)
const fetchAllBulkRecords = async (scope) => {
  const LIMIT = 100
  const allRecords = []
  let offset = 0
  let hasMore = true
  let fileAccessLevel = null

  while (hasMore) {
    // eslint-disable-next-line no-await-in-loop
    const response = await ResourceService.bulkGet(scope, offset, LIMIT)
    const batch = response.data || []
    allRecords.push(...batch)

    if (offset === 0) fileAccessLevel = response.fileAccessLevel

    hasMore = batch.length === LIMIT
    offset += LIMIT
  }
  return { allRecords, fileAccessLevel }
}

const executeBulkExport = async (
  scope,
  filename,
  password,
  t,
  messageApi,
  onDestroyParentModals,
) => {
  let progressModal = null

  const destroyProgress = () => {
    progressModal?.destroy()
    progressModal = null
  }

  const isEncrypted = !!password
  const exportedFilename = `${filename}${isEncrypted ? '.json.enc' : '.json'}`

  try {
    const { allRecords, fileAccessLevel } = await fetchAllBulkRecords(scope)

    const allowFileDownload = fileAccessLevel === FILE_ACCESS_LEVEL.DOWNLOAD_AND_PREVIEW
    if (!allowFileDownload) {
      onDestroyParentModals?.()
      const confirmed = await showFileAccessLimitModal(t)
      if (!confirmed) return false
    }

    if (allRecords.length === 0) {
      onDestroyParentModals?.()
      messageApi.error(t('topBar.bulkExport.noRecords'))
      return false
    }

    const fileHandle = await pickSaveFileHandle(exportedFilename, isEncrypted)
    if (fileHandle === null) return false

    progressModal = Modal.confirm({
      title: t('topBar.bulkExport.loading'),
      icon: null,
      width: 480,
      content: makeProgressContent(0, allRecords.length, t),
      closable: false,
      keyboard: false,
      maskClosable: false,
      okButtonProps: { style: { display: 'none' } },
      cancelButtonProps: { style: { display: 'none' } },
      wrapClassName: classNames('om-popup-1', styles.progressModalWrapper),
    })

    const startTime = Date.now()

    const writable = fileHandle
      ? await fileHandle.createWritable()
      : streamSaver.createWriteStream(exportedFilename).getWriter()

    const { failedRecords, omittedFileCount } = isEncrypted
      ? await streamEncryptedRecords(
          writable,
          allRecords,
          password,
          progressModal,
          t,
          allowFileDownload,
        )
      : await streamRecords(writable, allRecords, progressModal, t, allowFileDownload)

    destroyProgress()
    onDestroyParentModals?.()

    // 제외 안내는 실패 모달과 **독립적으로** 띄운다 — 실패가 있어도 "왜 그 파일이 없는지"는
    // 설명해야 하고, 반대로 실패가 없어도 제외는 알려야 한다
    if (omittedFileCount > 0) {
      messageApi.warning(
        t('record.desc.exportFileExcluded', {
          count: omittedFileCount,
          limit: formatSizeLimitLabel(EXPORTABLE_FILE_SIZE_LIMIT),
        }),
      )
    }

    if (failedRecords.length > 0) {
      showFailedRecordsModal(failedRecords, t)
    } else {
      showExportResultModal(
        isEncrypted
          ? t('record.result.exportEncryptedSuccessWithCount', { count: allRecords.length })
          : t('record.result.exportSuccessWithCount', { count: allRecords.length }),
        t,
        Date.now() - startTime,
      )
    }
    return true
  } catch (err) {
    console.error('Bulk export error:', err)
    destroyProgress()
    onDestroyParentModals?.()
    messageApi.error(t('topBar.bulkExport.apiFailed'))
    return false
  }
}

// ── CSV 내보내기(spec D1~D12) ──────────────────────────────────────────────────

// 템플릿이 없는 레코드 그룹(templateId null)을 선택 목록에서 가리키는 키
const NO_TEMPLATE_KEY = '__none__'

// 내보낼 템플릿 고르기(spec D8) — 선택된 key 배열, 취소·닫기는 null. 확인·취소 버튼은 TemplateSelectContent 안에 있다.
// resolve 는 처음 한 번만 효력이 있어 확인을 연타해도 흐름이 두 번 시작되지 않는다
const showTemplateSelectModal = ({ t, groups }) =>
  new Promise((resolve) => {
    const modalRef = { current: null }
    modalRef.current = confirmBase({
      icon: 'black',
      title: t('record.title.exportCsvSelectTemplates'),
      width: 480,
      wrapClassName: styles.customFooterModal,
      content: (
        <TemplateSelectContent
          t={t}
          groups={groups}
          onConfirm={(keys) => {
            modalRef.current?.destroy()
            resolve(keys)
          }}
          onCancel={() => {
            modalRef.current?.destroy()
            resolve(null)
          }}
        />
      ),
      okButtonProps: { style: { display: 'none' } },
      cancelButtonProps: { style: { display: 'none' } },
      onCancel: () => resolve(null),
    })
  })

export const executeBulkCsvExport = async (
  scope,
  filename,
  t,
  messageApi,
  onDestroyParentModals,
) => {
  const enc = new TextEncoder()
  let progressModal = null
  let writable = null
  let written = 0

  const destroyProgress = () => {
    progressModal?.destroy()
    progressModal = null
  }

  try {
    const { allRecords } = await fetchAllBulkRecords(scope)
    const exportable = allRecords.filter(isCsvExportableRecord)
    const memoCount = allRecords.length - exportable.length
    if (exportable.length === 0) {
      onDestroyParentModals?.()
      messageApi.error(t('topBar.bulkExport.noRecords'))
      if (memoCount > 0) {
        messageApi.warning(t('record.desc.exportCsvMemoExcluded', { n: memoCount }))
      }
      return false
    }

    const schemaByTemplateId = await loadTemplateSchemas([
      ...new Set(exportable.map(getTemplateId).filter(Boolean)),
    ])
    const locale = (i18n.language || 'ko-KR').replace('-', '_')

    // 템플릿이 2종 이상이면 내보낼 템플릿을 고르게 한다(spec D8). 선택 결과가 1종이면 Template 열이 빠진다
    const templateGroups = groupRecordsByTemplate(exportable)
    let selectedRecords = exportable
    if (templateGroups.length >= 2) {
      const selectedKeys = await showTemplateSelectModal({
        t,
        groups: templateGroups.map((group) => ({
          key: group.templateId ?? NO_TEMPLATE_KEY,
          name: group.templateId
            ? getTemplateDisplayName(
                schemaByTemplateId[group.templateId]?.translations,
                locale,
                group.records[0].template?.name ||
                  schemaByTemplateId[group.templateId]?.templateName,
              )
            : t('record.desc.exportCsvTemplateNone'),
          count: group.records.length,
        })),
      })
      // 취소(ESC·바깥 클릭 포함)는 저장 위치 선택 취소(아래 pickSaveFileHandle)와 같이 부모 모달을 닫지 않는다 —
      // 실수로 닫아도 평문 확인 창에서 다시 [내보내기]를 누를 수 있어야 한다
      if (selectedKeys === null) return false
      selectedRecords = templateGroups
        .filter((group) => selectedKeys.includes(group.templateId ?? NO_TEMPLATE_KEY))
        .flatMap((group) => group.records)
    }

    const plan = buildCsvPlan(selectedRecords, {
      schemaByTemplateId,
      templateColumnHeader: t('record.field.exportCsvTemplateColumn'),
      extraColumnHeader: t('record.field.exportCsvOtherColumn'),
    })

    const exportedFilename = `${filename}.csv`
    const fileHandle = await pickSaveFileHandle(exportedFilename, false, 'csv')
    if (fileHandle === null) return false

    // executeBulkExport 의 진행 모달과 같은 옵션 — 제외한 메모를 센 진행률이 100% 에 못 닿지 않게 총계는 쓸 행 수다
    progressModal = Modal.confirm({
      title: t('topBar.bulkExport.loading'),
      icon: null,
      width: 480,
      content: makeProgressContent(0, plan.sortedRecords.length, t),
      closable: false,
      keyboard: false,
      maskClosable: false,
      okButtonProps: { style: { display: 'none' } },
      cancelButtonProps: { style: { display: 'none' } },
      wrapClassName: classNames('om-popup-1', styles.progressModalWrapper),
    })

    const startTime = Date.now()

    writable = fileHandle
      ? await fileHandle.createWritable()
      : streamSaver.createWriteStream(exportedFilename).getWriter()

    await writable.write(enc.encode(getCsvHeaderBlock(plan.headers)))

    // 첨부는 받지 않는다(allowFileDownload=false) — CSV 에는 첨부 열이 없고 접근 제한 확인도 필요 없다(spec D5)
    const { failedRecords } = await processRecordsLoop(
      plan.sortedRecords,
      progressModal,
      t,
      async (exportRecord) => {
        const templateName = getTemplateDisplayName(
          schemaByTemplateId[getTemplateId(exportRecord)]?.translations,
          locale,
          exportRecord.template.name,
        )
        const { cells, hasDecryptFailure } = buildCsvRow(exportRecord, plan, templateName)
        await writable.write(enc.encode(formatCsvLine(cells)))
        written += 1
        return hasDecryptFailure ? { failReason: 'decrypt' } : undefined
      },
      false,
    )

    await writable.close()

    destroyProgress()
    onDestroyParentModals?.()

    if (memoCount > 0) {
      messageApi.warning(t('record.desc.exportCsvMemoExcluded', { n: memoCount }))
    }

    if (failedRecords.length > 0) {
      showFailedRecordsModal(failedRecords, t)
    } else {
      showExportResultModal(
        t('record.result.exportCsvSuccessWithCount', { n: written }),
        t,
        Date.now() - startTime,
      )
    }
    return true
  } catch (err) {
    console.error('Bulk CSV export error:', err)
    destroyProgress()
    await abortWritableQuietly(writable)
    onDestroyParentModals?.()
    messageApi.error(t('topBar.bulkExport.apiFailed'))
    return false
  }
}

export const showSkipWarningModal = ({
  t,
  styles: modalStyles,
  encryptModalRef,
  onExport,
  isMemo = false,
}) => {
  const textKeys = getExportTextKeys(isMemo)
  const warningModalRef = { current: null }

  const renderContent = (isExporting) => (
    <div>
      <div className={modalStyles.warningModal}>
        {/* i18next-extract-disable-next-line */}
        <div className={modalStyles.question}>{t(textKeys.skipQuestion)}</div>
        {/* i18next-extract-disable-next-line */}
        <div className={modalStyles.warningText}>{t(textKeys.skipDesc)}</div>
      </div>
      <div className={modalStyles.encryptModalFooter}>
        <div />
        <div className={modalStyles.rightButtons}>
          <Button disabled={isExporting} onClick={() => warningModalRef.current.destroy()}>
            {t('record.button.no')}
          </Button>
          <Button
            type="primary"
            loading={isExporting}
            onClick={isExporting ? undefined : handleExport}
          >
            {t('record.button.export')}
          </Button>
        </div>
      </div>
    </div>
  )

  const setExporting = (isExporting) => {
    warningModalRef.current?.update({
      keyboard: !isExporting,
      maskClosable: !isExporting,
      content: renderContent(isExporting),
    })
  }

  const handleExport = async () => {
    setExporting(true)
    await onExport(warningModalRef, encryptModalRef)
    setExporting(false)
  }

  warningModalRef.current = confirmBase({
    icon: 'red',
    title: t('record.title.exportPasswordsToFile'),
    width: 520,
    wrapClassName: modalStyles.customFooterModal,
    content: renderContent(false),
    okButtonProps: { style: { display: 'none' } },
    cancelButtonProps: { style: { display: 'none' } },
  })
}

export const showEncryptModal = ({
  t,
  styles: modalStyles,
  messageApi,
  onEncryptExport,
  onPlainExport,
  isMemo = false,
}) => {
  const passwordRef = { value: '', confirm: '' }
  const modalRef = { current: null }

  const renderContent = (isExporting) => (
    <div className={modalStyles.encryptModalContent}>
      <div className={modalStyles.encryptModal}>
        <div className={modalStyles.description}>{t('record.desc.exportSetPassword')}</div>
        <div className={modalStyles.passwordField}>
          <PasswordInput
            disabled={isExporting}
            onChange={(e) => {
              passwordRef.value = e.target.value
            }}
            onPressEnter={isExporting ? undefined : handleEncryptExport}
            placeholder={t('record.field.enterPassword')}
            maxLength={50}
          />
        </div>
        <div className={modalStyles.passwordField}>
          <PasswordInput
            disabled={isExporting}
            onChange={(e) => {
              passwordRef.confirm = e.target.value
            }}
            onPressEnter={isExporting ? undefined : handleEncryptExport}
            placeholder={t('record.field.confirmPassword')}
            maxLength={50}
          />
        </div>
      </div>
      <div className={modalStyles.encryptModalFooter}>
        <Button
          disabled={isExporting}
          onClick={() =>
            showSkipWarningModal({
              t,
              styles: modalStyles,
              encryptModalRef: modalRef,
              onExport: onPlainExport,
              isMemo,
            })
          }
        >
          {t('record.button.skip')}
        </Button>
        <div className={modalStyles.rightButtons}>
          <Button
            disabled={isExporting}
            onClick={() => modalRef.current.destroy()}
            className={classNames('h38')}
          >
            {t('record.button.no')}
          </Button>
          <Button
            type="primary"
            loading={isExporting}
            onClick={isExporting ? undefined : handleEncryptExport}
            className={classNames('type7', 'h38')}
          >
            {t('record.button.export')}
          </Button>
        </div>
      </div>
    </div>
  )

  const setExporting = (isExporting) => {
    modalRef.current?.update({
      keyboard: !isExporting,
      maskClosable: !isExporting,
      content: renderContent(isExporting),
    })
  }

  const handleEncryptExport = async () => {
    const validatePassword = (value) => {
      if (!value) return t('record.desc.exportEncryptDesc')
      if (value.length < 4) return t('validation.minLength.invalid', { length: 4 })
      if (/[^\x21-\x7e]/.test(value)) return t('validation.asciiPrintable.invalid')
      return null
    }

    const valueError = validatePassword(passwordRef.value)
    if (valueError) {
      messageApi.error(valueError)
      return
    }

    const confirmError = validatePassword(passwordRef.confirm)
    if (confirmError) {
      messageApi.error(confirmError)
      return
    }

    if (passwordRef.value !== passwordRef.confirm) {
      messageApi.error(t('record.desc.passwordMismatch'))
      return
    }

    setExporting(true)
    const password = passwordRef.value
    const completed = await onEncryptExport(password, modalRef)
    if (completed !== false) {
      passwordRef.value = ''
      passwordRef.confirm = ''
    } else {
      setExporting(false)
    }
  }

  modalRef.current = confirmBase({
    icon: 'red',
    title: t('record.title.exportPasswordsToFile'),
    width: 520,
    wrapClassName: modalStyles.customFooterModal,
    content: renderContent(false),
    okButtonProps: { style: { display: 'none' } },
    cancelButtonProps: { style: { display: 'none' } },
  })
}

export const getFileTimestamp = () => {
  const now = new Date()
  const yy = String(now.getFullYear()).slice(-2)
  const MM = String(now.getMonth() + 1).padStart(2, '0')
  const dd = String(now.getDate()).padStart(2, '0')
  const HH = String(now.getHours()).padStart(2, '0')
  const mm = String(now.getMinutes()).padStart(2, '0')
  return `${yy}${MM}${dd}${HH}${mm}`
}

export const showFilenameConfirmModal = ({
  t,
  styles: modalStyles,
  defaultFilename,
  onConfirm,
  isMemo = false,
  // 있을 때만 파일 형식(JSON/CSV) 선택을 보인다 — 호출부가 만든 { value } 에 선택이 적힌다(spec D1)
  formatRef,
}) => {
  const textKeys = getExportTextKeys(isMemo)

  confirmBase({
    icon: 'red',
    /* i18next-extract-disable-next-line */
    title: t(textKeys.confirmTitle),
    width: 480,
    content: (
      <div className={modalStyles.exportModal}>
        {/* i18next-extract-disable-next-line */}
        <div className={modalStyles.title}>{t(textKeys.confirmQuestion)}</div>
        {formatRef && <ExportFormatField t={t} formatRef={formatRef} />}
      </div>
    ),
    okText: t('common.button.yes'),
    cancelText: t('common.button.no'),
    okButtonProps: { className: classNames('type7', 'w100', 'h38') },
    onOk: () => onConfirm(defaultFilename),
  })
}

// 파일명 확인 → 암호화 여부 → 실행까지 이어지는 공통 export 플로우
// onExecute({ filename, password, onDestroyParentModals }): 실제 export 실행 함수
// CSV 를 고르면 onExecute({ filename, password: null, format: 'csv', onDestroyParentModals }) 로 불린다(JSON 경로는 format 키가 없다)
export const runExportFlow = ({
  defaultFilename,
  t,
  styles: modalStyles,
  messageApi,
  onExecute,
  // 단건 메모 내보내기면 확인·경고 문구의 종류 단어를 "메모"로 바꾼다 (일괄 내보내기는 false)
  isMemo = false,
  // CSV 선택지를 보일지(기본 false — 기존 호출부의 동작이 그대로다). 메모 단건은 끈다(spec D10)
  csvAllowed = false,
}) => {
  const formatRef = { value: 'json' }
  showFilenameConfirmModal({
    t,
    styles: modalStyles,
    defaultFilename,
    isMemo,
    formatRef: csvAllowed ? formatRef : undefined,
    onConfirm: (filename) =>
      csvAllowed && formatRef.value === 'csv'
        ? // CSV 는 암호화하지 않는다(spec D2) — 비밀번호 단계를 건너뛰고 기존 평문 저장 확인을 그대로 거친다.
          // showSkipWarningModal 은 encryptModalRef 를 역참조하지 않고 onExport 로 넘기기만 한다
          showSkipWarningModal({
            t,
            styles: modalStyles,
            encryptModalRef: { current: null },
            isMemo,
            onExport: async (warningModalRef) => {
              await onExecute({
                filename,
                password: null,
                format: 'csv',
                onDestroyParentModals: () => warningModalRef.current?.destroy(),
              })
            },
          })
        : showEncryptModal({
            t,
            styles: modalStyles,
            messageApi,
            isMemo,
            onEncryptExport: async (password, encryptModalRef) => {
              const completed = await onExecute({ filename, password })
              if (completed !== false) encryptModalRef.current?.destroy()
              return completed
            },
            onPlainExport: async (warningModalRef, encryptRef) => {
              const completed = await onExecute({
                filename,
                password: null,
                onDestroyParentModals: () => warningModalRef.current?.destroy(),
              })
              if (completed !== false) encryptRef.current?.destroy()
            },
          }),
  })
}

const handleBulkExport = (scope, t, messageApi) => {
  runExportFlow({
    defaultFilename: `${SERVICE_TITLE}-${getFileTimestamp()}`,
    t,
    styles,
    messageApi,
    csvAllowed: true,
    onExecute: ({ filename, password, onDestroyParentModals, format }) =>
      format === 'csv'
        ? executeBulkCsvExport(scope, filename, t, messageApi, onDestroyParentModals)
        : executeBulkExport(scope, filename, password, t, messageApi, onDestroyParentModals),
  })
}

export default handleBulkExport
